import { MouseEventHandler, ReactNode, useState } from 'react';

import { Stack, SxProps, Theme } from '@mui/material';
import { ChevronDownIcon, MoreVerticalIcon } from 'icons';
import { partition } from 'lodash-es';

import { mergeSxProps } from '../../../theme';
import { Menu, MenuProps } from '../../Menu';
import { isMenuItemWithText } from '../../Menu/types';
import { IconButtonWithTooltip, IconButtonWithTooltipProps } from '../IconButtonWithTooltip';

import type { IconButtonVariant } from '../IconButton/types';

type Props = Omit<IconButtonWithTooltipProps, 'title' | 'ref'> &
  Pick<MenuProps, 'anchorOrigin' | 'transformOrigin' | 'items' | 'activeIndex'> & {
    'data-testid'?: string;
    menuSx?: SxProps<Theme> | undefined;
    variant?: IconButtonVariant;
    Icon?: ReactNode;
    title?: string;
    showChevron?: boolean;
    onOpenChange?(value: boolean): void;
    inlineSingleItem?: boolean;
  };

export const ButtonMenu = ({
  anchorOrigin,
  transformOrigin,
  menuSx,
  isLoading,
  size,
  variant = 'text',
  Icon = <MoreVerticalIcon />,
  color = variant === 'text' ? 'text' : 'light',
  title = 'More actions',
  TooltipProps,
  showChevron,
  sx,
  disabled,
  onOpenChange,
  items,
  inlineSingleItem,
  ...props
}: Props) => {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const testId = props['data-testid'] || 'button-menu';

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    setAnchor(event.currentTarget);
    onOpenChange?.(true);
  };

  const handleClose = () => {
    setAnchor(null);
  };

  const [inlineActions, moreActions] =
    inlineSingleItem && items.length === 1 ? [items, []] : partition(items, 'inline');

  if (!items.length) {
    return null;
  }

  return (
    <>
      {inlineActions.filter(isMenuItemWithText).map((action, index) => (
        <IconButtonWithTooltip
          key={`inline-action-${index}`}
          title={action.text}
          onClick={(event) => {
            event.stopPropagation();
            action.onClick?.();
          }}
          disabled={disabled}
          size={size}
          variant={variant}
          color={action.severity === 'danger' ? 'error' : color}
          isLoading={isLoading}
          sx={sx}
        >
          {action.icon}
        </IconButtonWithTooltip>
      ))}
      {!!moreActions.length && (
        <>
          <IconButtonWithTooltip
            disabled={disabled}
            onClick={handleClick}
            variant={variant}
            size={size}
            className={anchor ? 'active' : undefined}
            color={color}
            title={title}
            TooltipProps={{ placement: 'bottom-end', ...TooltipProps }}
            isLoading={isLoading}
            data-testid={testId}
            sx={mergeSxProps(
              ({ palette }) => ({
                width: showChevron ? 'initial' : undefined,
                ...(palette.mode === 'dark' && {
                  color: palette.common.white,
                }),
              }),
              sx
            )}
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              {Icon}
              {showChevron && <ChevronDownIcon />}
            </Stack>
          </IconButtonWithTooltip>

          <Menu
            sx={menuSx}
            anchor={anchor}
            onClose={handleClose}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            onTransitionExited={() => onOpenChange?.(false)}
            items={moreActions}
            {...props}
          />
        </>
      )}
    </>
  );
};
